/**
 * PATH: packages/tenant-shared/src/settings/QuotechBrandingFooterPreview.tsx
 * PURPOSE: A faithful, read-only preview of the "Powered by Quotech" GTM footer
 *          that the backend appends to customer-facing sends, so an operator can
 *          SEE exactly how it sits at the bottom of their emails before deciding
 *          to keep or disable it in Settings → Email.
 * WHY: Operator ask — "make it visible at the bottom of your emails on how it
 *      will be placed". The real footer is server-side
 *      (organisation/services/email_signature.py :: signature_html); this mirrors
 *      its markup (inline styles, mascot GIF, teal pill CTA) so the preview
 *      matches the delivered email. Keep the two in sync if the footer changes.
 * NOTE: Inline styles (not Tailwind) on the footer itself so the preview renders
 *       byte-for-byte like the email-client-safe HTML, independent of app theme.
 */
import type { CSSProperties } from 'react'

// Mirrors organisation/services/email_signature.py brand constants.
const MASCOT_IMG_URL = 'https://dashboard.quotech.ai/mascot/beaver-foreman-v2.gif'
const GTM_TAGLINE = 'The AI Construction OS for Tradies and Builders.'
const GTM_CTA = 'See Quotech in action'

export interface QuotechBrandingFooterPreviewProps {
  /** When false the footer is dimmed to read as "won't be added". */
  enabled?: boolean
  className?: string
}

const cardStyle: CSSProperties = {
  fontFamily: "'Segoe UI', Arial, sans-serif",
  background: '#ffffff',
  borderRadius: 10,
  border: '1px solid #e8edf3',
  padding: '14px 18px',
}

export function QuotechBrandingFooterPreview({
  enabled = true,
  className,
}: QuotechBrandingFooterPreviewProps) {
  return (
    <div className={className}>
      {/* "Email canvas" frame so the footer reads as the bottom of a message. */}
      <div
        style={{
          ...cardStyle,
          opacity: enabled ? 1 : 0.45,
          filter: enabled ? 'none' : 'grayscale(1)',
          transition: 'opacity .15s ease, filter .15s ease',
        }}
        aria-hidden={!enabled}
      >
        {/* Faux preceding message line so the footer's position is obvious. */}
        <div style={{ fontSize: 13, color: '#9aa3af', lineHeight: 1.5 }}>
          …the rest of your email,
        </div>

        {/* The footer — mirrors signature_html() in email_signature.py. */}
        <div style={{ maxWidth: 560, margin: '22px 0 4px 0' }}>
          <div style={{ borderTop: '1px solid #e8edf3', paddingTop: 16 }}>
            <table role="presentation" cellPadding={0} cellSpacing={0} style={{ borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ verticalAlign: 'top', paddingRight: 14 }}>
                    <img
                      src={MASCOT_IMG_URL}
                      alt="Quotech beaver"
                      width={46}
                      height={50}
                      style={{ display: 'block', border: 0, width: 46, height: 50 }}
                    />
                  </td>
                  <td style={{ verticalAlign: 'top', paddingRight: 16 }}>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: '#1a1a2e',
                        lineHeight: 1.4,
                        letterSpacing: '.2px',
                      }}
                    >
                      Powered by <span style={{ color: '#0d9488' }}>Quotech</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.45, paddingTop: 4 }}>
                      {GTM_TAGLINE}
                    </div>
                  </td>
                  <td style={{ verticalAlign: 'top' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        background: '#0d9488',
                        color: '#ffffff',
                        fontSize: 12.5,
                        fontWeight: 700,
                        lineHeight: 1,
                        padding: '11px 20px',
                        borderRadius: 8,
                        boxShadow: '0 3px 8px rgba(13,148,136,0.38)',
                        whiteSpace: 'nowrap',
                        letterSpacing: '.3px',
                      }}
                    >
                      {GTM_CTA}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default QuotechBrandingFooterPreview
